import { Loader2, Calendar, Trophy } from 'lucide-react';
import { Tooltip, KPI_TOOLTIPS } from '@/components/ui';
import { formatCurrency, calculateRepPerformance, calculateLeadToDealConversion } from '@/utils';
import type { AnalyticsDetailData } from './AnalyticsDetailModal';
import type { Lead } from '@/types';

interface RepLeaderboardProps {
  data: ReturnType<typeof calculateRepPerformance>;
  isLoading?: boolean;
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  defaultDateRange: { start: string; end: string };
  leads: Lead[];
  leadToDealConversion: ReturnType<typeof calculateLeadToDealConversion>;
  onMetricClick?: (data: AnalyticsDetailData) => void;
}

export function RepLeaderboard({
  data,
  isLoading,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  defaultDateRange,
  leads,
  leadToDealConversion,
  onMetricClick,
}: RepLeaderboardProps) {
  const hasCustomDateFilter = startDate !== defaultDateRange.start || endDate !== defaultDateRange.end;

  const resetDateFilter = () => {
    onStartDateChange(defaultDateRange.start);
    onEndDateChange(defaultDateRange.end);
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-[#F78C4E]" />
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="h-5 w-5 text-[#F78C4E]" />
          <Tooltip {...KPI_TOOLTIPS.repPerformance}>
            <h2 className="text-lg font-semibold text-white">Rep Leaderboard</h2>
          </Tooltip>
        </div>
        <p className="text-center text-zinc-500 py-4">No rep data available</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-[#F78C4E]" />
          <Tooltip {...KPI_TOOLTIPS.repPerformance}>
            <h2 className="text-lg font-semibold text-white">Rep Leaderboard</h2>
          </Tooltip>
          <span className="text-xs text-zinc-500">({data.length} reps)</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[#F78C4E]" />
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#F78C4E] focus:ring-1 focus:ring-[#F78C4E]/50"
          />
          <span className="text-zinc-400 text-xs">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#F78C4E] focus:ring-1 focus:ring-[#F78C4E]/50"
          />
          {hasCustomDateFilter && (
            <button
              onClick={resetDateFilter}
              className="text-xs text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-zinc-700"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-700">
              <th className="text-left py-2 px-3 text-zinc-400 font-medium">#</th>
              <th className="text-left py-2 px-3 text-zinc-400 font-medium">Rep</th>
              <th className="text-center py-2 px-3 text-zinc-400 font-medium">Leads</th>
              <th className="text-center py-2 px-3 text-zinc-400 font-medium">Converted</th>
              <th className="text-center py-2 px-3 text-zinc-400 font-medium">
                <Tooltip {...KPI_TOOLTIPS.conversionRate}>
                  <span>Conv. Rate</span>
                </Tooltip>
              </th>
              <th className="text-center py-2 px-3 text-zinc-400 font-medium">Won Deals</th>
              <th className="text-right py-2 px-3 text-zinc-400 font-medium">Won Value</th>
              <th className="text-right py-2 px-3 text-zinc-400 font-medium">Pipeline</th>
              <th className="text-center py-2 px-3 text-zinc-400 font-medium">Resp. Time</th>
            </tr>
          </thead>
          <tbody>
            {data.map((rep, index) => {
              const repLeads = leads.filter(l => l.owner === rep.name);
              const repConversions = leadToDealConversion.convertedLeads.filter(c => c.lead.owner === rep.name);
              const repWonDeals = repConversions.filter(c => {
                const stage = (c.deal.stage || '').toLowerCase();
                return stage.includes('won') && !stage.includes('lost');
              });
              const repPipelineDeals = repConversions.filter(c => {
                const stage = (c.deal.stage || '').toLowerCase();
                return !stage.includes('won') && !stage.includes('lost') && !stage.includes('closed');
              });

              const handleLeadsClick = () => {
                if (!onMetricClick || rep.totalLeads === 0) return;
                onMetricClick({
                  title: `${rep.name || 'Unassigned'}'s Leads`,
                  subtitle: `${rep.totalLeads} total leads`,
                  leads: repLeads,
                });
              };

              const handleConvertedClick = () => {
                if (!onMetricClick || rep.convertedLeads === 0) return;
                onMetricClick({
                  title: `${rep.name || 'Unassigned'}'s Converted Leads`,
                  subtitle: `${rep.convertedLeads} leads converted to deals`,
                  leads: repConversions.map(c => c.lead),
                });
              };

              const handleWonDealsClick = () => {
                if (!onMetricClick || rep.wonDeals === 0) return;
                onMetricClick({
                  title: `${rep.name || 'Unassigned'}'s Won Deals`,
                  subtitle: `${rep.wonDeals} deals worth ${formatCurrency(rep.wonValue)}`,
                  deals: repWonDeals.map(c => c.deal),
                });
              };

              const handlePipelineClick = () => {
                if (!onMetricClick || repPipelineDeals.length === 0) return;
                onMetricClick({
                  title: `${rep.name || 'Unassigned'}'s Pipeline`,
                  subtitle: `${repPipelineDeals.length} active deals worth ${formatCurrency(rep.totalPipeline)}`,
                  deals: repPipelineDeals.map(c => c.deal),
                });
              };

              return (
                <tr
                  key={rep.name}
                  className={`border-b border-zinc-800 ${index === 0 ? 'bg-[#F78C4E]/5' : ''}`}
                >
                  <td className="py-3 px-3">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                      index === 0 ? 'bg-[#F78C4E] text-white' :
                      index === 1 ? 'bg-zinc-600 text-white' :
                      index === 2 ? 'bg-amber-700 text-white' :
                      'bg-zinc-800 text-zinc-400'
                    }`}>
                      {index + 1}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <span className="font-medium text-white">{rep.name || 'Unassigned'}</span>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <button
                      onClick={handleLeadsClick}
                      disabled={rep.totalLeads === 0}
                      className={`${rep.totalLeads > 0 ? 'text-zinc-300 hover:text-white hover:underline cursor-pointer' : 'text-zinc-500 cursor-default'}`}
                    >
                      {rep.totalLeads}
                    </button>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <button
                      onClick={handleConvertedClick}
                      disabled={rep.convertedLeads === 0}
                      className={`${rep.convertedLeads > 0 ? 'text-zinc-300 hover:text-white hover:underline cursor-pointer' : 'text-zinc-500 cursor-default'}`}
                    >
                      {rep.convertedLeads}
                    </button>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      rep.conversionRate >= 30 ? 'bg-emerald-500/20 text-emerald-400' :
                      rep.conversionRate >= 15 ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-zinc-700 text-zinc-400'
                    }`}>
                      {rep.conversionRate}%
                    </span>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <button
                      onClick={handleWonDealsClick}
                      disabled={rep.wonDeals === 0}
                      className={`${rep.wonDeals > 0 ? 'text-zinc-300 hover:text-white hover:underline cursor-pointer' : 'text-zinc-500 cursor-default'}`}
                    >
                      {rep.wonDeals}
                    </button>
                  </td>
                  <td className="py-3 px-3 text-right">
                    <button
                      onClick={handleWonDealsClick}
                      disabled={rep.wonValue === 0}
                      className={`font-semibold ${rep.wonValue > 0 ? 'text-emerald-400 hover:text-emerald-300 hover:underline cursor-pointer' : 'text-zinc-500 cursor-default'}`}
                    >
                      {formatCurrency(rep.wonValue)}
                    </button>
                  </td>
                  <td className="py-3 px-3 text-right">
                    <button
                      onClick={handlePipelineClick}
                      disabled={rep.totalPipeline === 0}
                      className={`${rep.totalPipeline > 0 ? 'text-zinc-300 hover:text-white hover:underline cursor-pointer' : 'text-zinc-500 cursor-default'}`}
                    >
                      {formatCurrency(rep.totalPipeline)}
                    </button>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className={`text-xs ${
                      rep.avgResponseTime <= 1 ? 'text-emerald-400' :
                      rep.avgResponseTime <= 3 ? 'text-yellow-400' :
                      'text-orange-400'
                    }`}>
                      {rep.avgResponseTime}d
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
